/**
 * Device type -> Style01 trait mapping and modal render resolver.
 */
import {
  hasDeviceTypeStyles,
  parseDeviceTypeFromCanonicalStyleId,
  parseRawStyleIdFromCanonical,
  toCanonicalDeviceTypeStyleId,
} from '../../components/deviceTypeStyles'
import { getStyleIdsForState, getStyleIdsForTrait } from '../../components/traitStyles'
import { getStyleIdsForChart, pickChartStyleForDeviceType } from '../../components/datacharts'
import type { FramePreset } from '../../services/appfactoryFirestore'
import type { DeviceTypeModalOverrideForContext } from './DeviceTypeModalOverridesContext'

export type DeviceTypeStyleTrait = { trait: string; styleId: string }

export type ModalRenderConfig = {
  enabledTraits?: string[]
  enabledStates?: string[]
  enabledCharts?: string[]
  stateDisplayOverrides?: Record<string, { icon?: string; label?: string; unit?: string }>
  componentOrder?: string[]
  hiddenComponents?: string[]
  modalItemOrder?: string[]
  modalTraitStyles?: Record<string, string>
  modalStateStyles?: Record<string, string>
  modalChartStyles?: Record<string, string>
  modalItemVisible?: Record<string, boolean>
  modalItemSpacing?: Record<string, number>
  modalFrameStyles?: Record<string, FramePreset>
  modalItemCss?: Record<string, string>
  modalBackground?: 'transparent' | string
  modalStyleId?: string
}

export type ResolveDeviceModalRenderInput = {
  rawDeviceType: string
  rawTraits?: Array<string | unknown>
  baseConfig?: ModalRenderConfig
  modalOverrides?: Record<string, DeviceTypeModalOverrideForContext>
  fallbackEnabledTraits?: string[]
  fallbackEnabledStates?: string[]
  fallbackEnabledCharts?: string[]
  forceAcDefaultStyle?: boolean
}

export type ResolveDeviceModalRenderResult = {
  config: ModalRenderConfig
  debug: {
    resolvedDeviceType: string
    styleDeviceType: string
    resolvedStyleId: string
    matchedOverrideKey: string
    powerStatusOffKey: string
  }
}

/** AC_UNIT Style01: 與 DeviceTypeModalPreview 一致，順序為 Power State → 室溫 → 目標溫度 → Thermostat Mode → Custom Modes → Fan Speed */
export const AC_UNIT_STYLE01: DeviceTypeStyleTrait[] = [
  { trait: 'OnOff', styleId: 'Style01' },
  { trait: 'Sensor', styleId: 'Style01' }, // 顯示 thermostatTemperatureAmbient（室溫）
  { trait: 'TemperatureSetting', styleId: 'Style05' },
  { trait: 'ThermostatSetMode', styleId: 'Style01' },
  { trait: 'CustomModes', styleId: 'Style01' },
  { trait: 'FanSpeed', styleId: 'Style01' },
]

const DEVICE_TYPE_STYLE01_MAP: Record<string, DeviceTypeStyleTrait[]> = {
  AC_UNIT: AC_UNIT_STYLE01,
  HVAC: AC_UNIT_STYLE01, // hvac 與 ac_unit 共用同一組 traits
}

const TRAIT_STYLE01_PRIORITY = AC_UNIT_STYLE01.map((item) => item.trait)

export function getDeviceTypeStyle01Traits(deviceType: string): DeviceTypeStyleTrait[] {
  const key = (deviceType?.trim() || '').toUpperCase()
  return DEVICE_TYPE_STYLE01_MAP[key] ?? []
}

function uniqueTokens(values: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of values) {
    const token = String(raw || '').trim()
    if (!token) continue
    if (seen.has(token)) continue
    seen.add(token)
    result.push(token)
  }
  return result
}

export function pickDefaultTraitStyleId(traitId: string): string {
  const styleIds = getStyleIdsForTrait(traitId)
  if (styleIds.length === 0) return 'Style01'
  if ((traitId === 'TemperatureSetting' || traitId === 'TemperatureControl') && styleIds.includes('Style05')) {
    return 'Style05'
  }
  if (styleIds.includes('Style01')) return 'Style01'
  return styleIds[0]
}

export function pickDefaultStateStyleId(stateKey: string): string {
  const styleIds = getStyleIdsForState(stateKey)
  if (styleIds.length === 0) return 'Style01'
  const normalizedStateKey = String(stateKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
  if (
    (normalizedStateKey === 'lux' ||
      normalizedStateKey === 'illuminance' ||
      normalizedStateKey === 'lightlevel') &&
    styleIds.includes('Style05')
  ) {
    return 'Style05'
  }
  if (styleIds.includes('Style01')) return 'Style01'
  return styleIds[0]
}

export function pickDefaultChartStyleId(chartId: string): string {
  const styleIds = getStyleIdsForChart(chartId)
  if (styleIds.length === 0) return 'Style01'
  const preferred = pickChartStyleForDeviceType(chartId, '')
  if (preferred && styleIds.includes(preferred)) return preferred
  if (styleIds.includes('Style01')) return 'Style01'
  return styleIds[0]
}

export function pickDefaultChartStyleIdByDeviceType(chartId: string, deviceType: string): string {
  const styleIds = getStyleIdsForChart(chartId)
  if (styleIds.length === 0) return 'Style01'
  const preferred = pickChartStyleForDeviceType(chartId, deviceType)
  if (preferred && styleIds.includes(preferred)) return preferred
  if (styleIds.includes('Style01')) return 'Style01'
  return styleIds[0]
}

function orderTraitsByStyle01Priority(traits: string[]): string[] {
  const normalized = uniqueTokens(traits)
  const ordered = TRAIT_STYLE01_PRIORITY.filter((trait) => normalized.includes(trait))
  const rest = normalized.filter((trait) => !ordered.includes(trait))
  return [...ordered, ...rest]
}

/** 與 ModalStyleEditor 一致的預設 item order，用於 clone 時建立完整 payload */
export function buildDefaultItemOrder(
  enabledTraits: string[],
  enabledStates: string[],
  enabledCharts: string[]
): string[] {
  const traitOrder = AC_UNIT_STYLE01.map((t) => t.trait)
  const orderedTraits = traitOrder.filter((t) => enabledTraits.includes(t))
  const restTraits = enabledTraits.filter((t) => !traitOrder.includes(t))
  const traits = [...orderedTraits, ...restTraits]
  return [
    ...traits.map((t) => `trait:${t}`),
    ...enabledStates.map((s) => `state:${s}`),
    ...enabledCharts.map((c) => `chart:${c}`),
  ]
}

/** 與 DeviceTypeModalPreview 一致的預設 trait styles */
export function buildDefaultTraitStyles(
  deviceType: string,
  traits: string[]
): Record<string, string> {
  const style01Traits = getDeviceTypeStyle01Traits(deviceType)
  return Object.fromEntries(
    traits.map((t) => {
      const mapped = style01Traits.find((x) => x.trait === t)
      const fallbackStyleId = pickDefaultTraitStyleId(t)
      return [t, mapped?.styleId ?? fallbackStyleId]
    })
  )
}

export type Style01ModalSeedPayload = {
  enabledTraits: string[]
  enabledStates: string[]
  enabledCharts: string[]
  modalItemOrder: string[]
  modalTraitStyles: Record<string, string>
  modalStateStyles: Record<string, string>
  modalChartStyles: Record<string, string>
}

export function buildStyle01ModalSeedPayload(input: {
  deviceType: string
  traits?: string[]
  states?: string[]
  charts?: string[]
}): Style01ModalSeedPayload {
  const traits = orderTraitsByStyle01Priority(input.traits ?? [])
  const states = uniqueTokens(input.states ?? [])
  const charts = uniqueTokens(input.charts ?? [])
  return {
    enabledTraits: traits,
    enabledStates: states,
    enabledCharts: charts,
    modalItemOrder: buildDefaultItemOrder(traits, states, charts),
    modalTraitStyles: buildDefaultTraitStyles(input.deviceType, traits),
    modalStateStyles: Object.fromEntries(states.map((stateKey) => [stateKey, pickDefaultStateStyleId(stateKey)])),
    modalChartStyles: Object.fromEntries(
      charts.map((chartId) => [chartId, pickDefaultChartStyleIdByDeviceType(chartId, input.deviceType)])
    ),
  }
}

/** Mock state for AC_UNIT preview */
export const MOCK_AC_UNIT_STATE: Record<string, unknown> = {
  on: true,
  currentFanSpeedSetting: 'medium',
  currentModeSettings: { mode: 'cool' },
  currentMode: 'cool',
  thermostatTemperatureSetpoint: 24,
  thermostatTemperatureAmbient: 25,
  thermostatMode: 'cool',
  temp: 25,
  temperature: 24,
  value: 25,
  unit: '°C',
  sensorType: 'Room Temp',
}

function normalizeDeviceTypeToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function resolveModalTemplateDeviceType(
  rawDeviceType: string,
  traits?: Array<string | unknown>
): string {
  const token = normalizeDeviceTypeToken(rawDeviceType)
  if (!token) return ''
  if (token === 'power_meter') return 'electrical_sensor_1p3w'
  if (token === 'power_meter_3phase') return 'electrical_sensor_3p4w'
  if (token === 'electrical_sensor_1p3w' || token === 'electrical_sensor_3p4w') return token
  if (token === 'electrical_sensor') {
    const hint = `${rawDeviceType} ${(traits || []).map((item) => String(item || '')).join(' ')}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
    if (hint.includes('3p4w') || hint.includes('3 phase') || hint.includes('three phase')) {
      return 'electrical_sensor_3p4w'
    }
    if (hint.includes('1p3w') || hint.includes('single phase')) {
      return 'electrical_sensor_1p3w'
    }
  }
  if (token === 'ac_unit' || token === 'hvac') return 'ac_unit'
  if (token === 'air_conditioner' || token === 'airconditioner' || token === 'ac') return 'ac_unit'
  if (token.startsWith('ac_') || token.endsWith('_ac') || token.includes('aircon')) return 'ac_unit'
  const normalizedTraits = (traits ?? [])
    .map((t) => String(t || '').toLowerCase().replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
  if (
    normalizedTraits.some(
      (t) =>
        t.includes('temperaturesetting') ||
        t.includes('temperaturecontrol') ||
        t.includes('thermostat')
    ) &&
    normalizedTraits.some((t) => t.includes('fanspeed'))
  ) {
    return 'ac_unit'
  }
  return token
}

export function hasAcLikeTraits(traits?: Array<string | unknown>): boolean {
  const normalizedTraits = (traits ?? [])
    .map((t) => String(t || '').toLowerCase().replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
  return (
    normalizedTraits.some(
      (t) =>
        t.includes('temperaturesetting') ||
        t.includes('temperaturecontrol') ||
        t.includes('thermostat')
    ) &&
    normalizedTraits.some((t) => t.includes('fanspeed'))
  )
}

export function resolveModalConfigByDeviceType(
  configs: Record<string, ModalRenderConfig> | undefined,
  candidates: string[]
): { config?: ModalRenderConfig; matchedKey: string } {
  const dt = configs ?? {}
  for (const key of candidates) {
    if (!key) continue
    const exact = dt[key]
    if (exact) return { config: exact, matchedKey: key }
  }
  const normalizedMap = new Map<string, { key: string; config: ModalRenderConfig }>()
  for (const [k, v] of Object.entries(dt)) {
    const token = normalizeDeviceTypeToken(k)
    if (!token) continue
    normalizedMap.set(token, { key: k, config: v })
  }
  for (const key of candidates) {
    const token = normalizeDeviceTypeToken(key)
    if (!token) continue
    const found = normalizedMap.get(token)
    if (found) return { config: found.config, matchedKey: found.key }
  }
  return { config: undefined, matchedKey: '' }
}

function normalizeStyleIdToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function normalizeRawStyleId(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.toLowerCase().startsWith('types-')) return parseRawStyleIdFromCanonical(raw)
  const m = raw.match(/style(\d+)/i)
  if (m?.[1]) return `Style${m[1]}`
  return raw
}

function resolveCanonicalModalStyleId(
  value: string,
  deviceTypeHint: string
): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const matchedCanonical = raw.match(/types-[a-z0-9_]+-style\d+/i)?.[0]?.toLowerCase()
  if (matchedCanonical) return matchedCanonical
  const rawStyleId = normalizeRawStyleId(raw)
  const normalizedType = normalizeDeviceTypeToken(deviceTypeHint)
  const canonicalType = normalizedType === 'hvac' ? 'ac_unit' : normalizedType
  if (canonicalType && /^style\d+$/i.test(rawStyleId)) {
    return toCanonicalDeviceTypeStyleId(canonicalType, rawStyleId)
  }
  return raw.toLowerCase()
}

function hasEntries<T extends Record<string, unknown> | undefined>(value: T): boolean {
  return Boolean(value && Object.keys(value).length > 0)
}

function selectAvailableToken(
  preferredValues: string[],
  availableValues: string[]
): string | undefined {
  const normalized = new Map<string, string>()
  for (const value of availableValues) {
    const token = String(value || '').trim()
    if (!token) continue
    normalized.set(normalizeDeviceTypeToken(token), token)
  }
  for (const preferred of preferredValues) {
    const found = normalized.get(normalizeDeviceTypeToken(preferred))
    if (found) return found
  }
  return undefined
}

function resolveBuiltInModalPreset(input: {
  deviceType: string
  rawStyleId: string
  fallbackTraits: string[]
  fallbackStates: string[]
}): DeviceTypeModalOverrideForContext | undefined {
  const typeToken = normalizeDeviceTypeToken(input.deviceType)
  const styleToken = normalizeStyleIdToken(input.rawStyleId)
  if (styleToken !== 'style02') return undefined
  if (typeToken !== 'blinds' && typeToken !== 'curtain') return undefined

  const availableTraits = uniqueTokens(input.fallbackTraits ?? [])
  const preferredTraits = ['OpenClose', 'Modes', 'CustomModes']
  const traitOrder = preferredTraits
    .map((traitId) => selectAvailableToken([traitId], availableTraits))
    .filter((value): value is string => Boolean(value))
  if (traitOrder.length === 0) return undefined

  const positionState = selectAvailableToken(
    ['openClosePercent', 'openPercent', 'position'],
    uniqueTokens(input.fallbackStates ?? [])
  )

  const modalTraitStyles: Record<string, string> = {}
  if (traitOrder.some((trait) => normalizeDeviceTypeToken(trait) === normalizeDeviceTypeToken('OpenClose'))) {
    modalTraitStyles.OpenClose = 'Style03'
  }
  if (traitOrder.some((trait) => normalizeDeviceTypeToken(trait) === normalizeDeviceTypeToken('Modes'))) {
    modalTraitStyles.Modes = 'Style04'
  }
  if (traitOrder.some((trait) => normalizeDeviceTypeToken(trait) === normalizeDeviceTypeToken('CustomModes'))) {
    modalTraitStyles.CustomModes = 'Style03'
  }

  return {
    styleId: toCanonicalDeviceTypeStyleId(typeToken === 'curtain' ? 'blinds' : typeToken, 'Style02'),
    modalItemOrder: [
      ...(positionState ? [`state:${positionState}`] : []),
      ...traitOrder.map((traitId) => `trait:${traitId}`),
    ],
    modalTraitStyles,
  }
}

function resolveModalOverrideByStyleId(
  overrides: Record<string, DeviceTypeModalOverrideForContext>,
  requestedStyleId: string,
  deviceTypeHint?: string
): { key: string; override: DeviceTypeModalOverrideForContext } | undefined {
  const canonicalRequested = resolveCanonicalModalStyleId(requestedStyleId, deviceTypeHint || '')
  if (!canonicalRequested) return undefined
  if (overrides[canonicalRequested]) {
    return { key: canonicalRequested, override: overrides[canonicalRequested] }
  }
  const requestedToken = normalizeStyleIdToken(canonicalRequested)
  const requestedRawStyle = normalizeRawStyleId(canonicalRequested).toLowerCase()
  const requestedTypeToken = normalizeDeviceTypeToken(
    deviceTypeHint || parseDeviceTypeFromCanonicalStyleId(canonicalRequested)
  )
  const requestedTypeAliases = new Set<string>([requestedTypeToken])
  if (requestedTypeToken === 'ac_unit' || requestedTypeToken === 'hvac') {
    requestedTypeAliases.add('ac_unit')
    requestedTypeAliases.add('hvac')
  }
  for (const [key, override] of Object.entries(overrides)) {
    if (normalizeStyleIdToken(key) === requestedToken) return { key, override }
    if (override?.styleId && normalizeStyleIdToken(override.styleId) === requestedToken) {
      return { key, override }
    }
    if (requestedRawStyle && requestedTypeToken) {
      const keyType = normalizeDeviceTypeToken(parseDeviceTypeFromCanonicalStyleId(key))
      const keyRawStyle = normalizeRawStyleId(key).toLowerCase()
      if (
        keyType &&
        keyRawStyle &&
        requestedTypeAliases.has(keyType) &&
        keyRawStyle === requestedRawStyle
      ) {
        return { key, override }
      }
      if (override?.styleId) {
        const overrideType = normalizeDeviceTypeToken(
          parseDeviceTypeFromCanonicalStyleId(override.styleId)
        )
        const overrideRawStyle = normalizeRawStyleId(override.styleId).toLowerCase()
        if (
          overrideType &&
          overrideRawStyle &&
          requestedTypeAliases.has(overrideType) &&
          overrideRawStyle === requestedRawStyle
        ) {
          return { key, override }
        }
      }
    }
  }
  if (requestedRawStyle) {
    for (const [key, override] of Object.entries(overrides)) {
      const keyRawStyle = normalizeRawStyleId(key).toLowerCase()
      if (!keyRawStyle || keyRawStyle !== requestedRawStyle) continue
      const keyType = normalizeDeviceTypeToken(parseDeviceTypeFromCanonicalStyleId(key))
      if (requestedTypeAliases.size > 0 && keyType && requestedTypeAliases.has(keyType)) {
        return { key, override }
      }
      if (override?.styleId) {
        const overrideRawStyle = normalizeRawStyleId(override.styleId).toLowerCase()
        const overrideType = normalizeDeviceTypeToken(
          parseDeviceTypeFromCanonicalStyleId(override.styleId)
        )
        if (
          overrideRawStyle === requestedRawStyle &&
          overrideType &&
          requestedTypeAliases.has(overrideType)
        ) {
          return { key, override }
        }
      }
    }
  }
  return undefined
}

export function resolveDeviceModalRenderConfig(
  input: ResolveDeviceModalRenderInput
): ResolveDeviceModalRenderResult {
  const base = input.baseConfig ?? {}
  const overrides = input.modalOverrides ?? {}
  const resolvedDeviceType =
    resolveModalTemplateDeviceType(input.rawDeviceType, input.rawTraits) ||
    normalizeDeviceTypeToken(input.rawDeviceType)
  const acLike = hasAcLikeTraits(input.rawTraits)
  const shouldDefaultAcStyle = input.forceAcDefaultStyle !== false
  const resolvedTypeToken = normalizeDeviceTypeToken(resolvedDeviceType)
  const fallbackRawStyle = shouldDefaultAcStyle &&
    (resolvedTypeToken === 'ac_unit' || resolvedTypeToken === 'hvac' || acLike) &&
    !base.modalStyleId
    ? 'types-ac_unit-style01'
    : ''

  const resolvedStyleId = resolveCanonicalModalStyleId(
    base.modalStyleId || fallbackRawStyle,
    resolvedTypeToken
  )
  const styleDeviceType = resolvedStyleId.startsWith('types-')
    ? parseDeviceTypeFromCanonicalStyleId(resolvedStyleId)
    : resolvedTypeToken
  const rawStyleId = normalizeRawStyleId(resolvedStyleId)
  const overrideMatch = resolvedStyleId
    ? resolveModalOverrideByStyleId(overrides, resolvedStyleId, styleDeviceType || resolvedTypeToken)
    : undefined
  const override = overrideMatch?.override

  const styleTraits = getDeviceTypeStyle01Traits(styleDeviceType || resolvedTypeToken).map((t) => t.trait)
  const fallbackTraits = input.fallbackEnabledTraits ?? []
  const fallbackStates = input.fallbackEnabledStates ?? []
  const fallbackCharts = input.fallbackEnabledCharts ?? []
  const baseEnabledTraits =
    base.enabledTraits?.length
      ? base.enabledTraits
      : base.componentOrder?.length
        ? base.componentOrder
        : fallbackTraits
  const builtInPreset = !override
    ? resolveBuiltInModalPreset({
        deviceType: styleDeviceType || resolvedTypeToken,
        rawStyleId,
        fallbackTraits: baseEnabledTraits,
        fallbackStates: base.enabledStates ?? fallbackStates,
      })
    : undefined
  const traitsFromConfig = styleTraits.length > 0 ? styleTraits : baseEnabledTraits
  const useTraitCombo =
    (hasDeviceTypeStyles(styleDeviceType || resolvedTypeToken) ||
      hasDeviceTypeStyles(resolvedTypeToken) ||
      acLike) &&
    traitsFromConfig.length > 0

  const hiddenComponents = new Set(base.hiddenComponents ?? [])
  const mergedVisibleFromHidden = {
    ...(base.modalItemVisible ?? {}),
    ...Object.fromEntries(Array.from(hiddenComponents).map((id) => [`trait:${id}`, false])),
  }

  if (!useTraitCombo) {
    const passthroughConfig: ModalRenderConfig = {
      ...base,
      modalStyleId: resolvedStyleId || base.modalStyleId,
      modalItemVisible: mergedVisibleFromHidden,
      stateDisplayOverrides: base.stateDisplayOverrides,
    }
    const passthroughCss = passthroughConfig.modalItemCss ?? {}
    const passthroughPowerStatusOffKey =
      Object.keys(passthroughCss).find((key) => /::powerstatus@off$/i.test(key)) ?? ''
    return {
      config: passthroughConfig,
      debug: {
        resolvedDeviceType: resolvedTypeToken,
        styleDeviceType: styleDeviceType || resolvedTypeToken,
        resolvedStyleId: passthroughConfig.modalStyleId || '',
        matchedOverrideKey: overrideMatch?.key || '',
        powerStatusOffKey: passthroughPowerStatusOffKey,
      },
    }
  }

  const baseStateStyles = base.modalStateStyles ?? {}
  const baseChartStyles = base.modalChartStyles ?? {}
  const defaultOrder = buildDefaultItemOrder(
    traitsFromConfig,
    Object.keys(override?.modalStateStyles ?? baseStateStyles),
    Object.keys(override?.modalChartStyles ?? baseChartStyles)
  )
  const resolvedOrder =
    override?.modalItemOrder?.length
      ? override.modalItemOrder
      : builtInPreset?.modalItemOrder?.length
        ? builtInPreset.modalItemOrder
      : base.modalItemOrder?.length
        ? base.modalItemOrder
        : defaultOrder

  const traitOrder = resolvedOrder
    .filter((k) => k.startsWith('trait:'))
    .map((k) => k.replace(/^trait:/, ''))
  const stateOrder = resolvedOrder
    .filter((k) => k.startsWith('state:'))
    .map((k) => k.replace(/^state:/, ''))
  const chartOrder = resolvedOrder
    .filter((k) => k.startsWith('chart:'))
    .map((k) => k.replace(/^chart:/, ''))

  const componentOrder = traitOrder.length > 0 ? traitOrder : traitsFromConfig
  const traitStyles =
    hasEntries(override?.modalTraitStyles)
      ? (override?.modalTraitStyles as Record<string, string>)
      : hasEntries(builtInPreset?.modalTraitStyles)
        ? (builtInPreset?.modalTraitStyles as Record<string, string>)
      : hasEntries(base.modalTraitStyles)
        ? (base.modalTraitStyles as Record<string, string>)
        : buildDefaultTraitStyles(styleDeviceType || resolvedTypeToken, traitsFromConfig)
  const modalItemCss = override?.modalItemCss ?? base.modalItemCss ?? {}
  const powerStatusOffKey =
    Object.keys(modalItemCss).find((key) => /::powerstatus@off$/i.test(key)) ?? ''

  const resolvedConfig: ModalRenderConfig = {
    modalStyleId: resolvedStyleId || base.modalStyleId,
    modalItemOrder: resolvedOrder,
    enabledTraits: componentOrder,
    enabledStates: stateOrder.length > 0 ? stateOrder : (base.enabledStates ?? fallbackStates),
    enabledCharts: chartOrder.length > 0 ? chartOrder : (base.enabledCharts ?? fallbackCharts),
    componentOrder,
    hiddenComponents: [],
    modalTraitStyles: traitStyles,
    modalStateStyles: override?.modalStateStyles ?? base.modalStateStyles ?? {},
    modalChartStyles: override?.modalChartStyles ?? base.modalChartStyles ?? {},
    stateDisplayOverrides: base.stateDisplayOverrides,
    modalItemVisible: override?.modalItemVisible ?? builtInPreset?.modalItemVisible ?? mergedVisibleFromHidden,
    modalItemSpacing: override?.modalItemSpacing ?? builtInPreset?.modalItemSpacing ?? base.modalItemSpacing ?? {},
    modalFrameStyles: override?.modalFrameStyles ?? builtInPreset?.modalFrameStyles ?? base.modalFrameStyles ?? {},
    modalItemCss,
    modalBackground: override?.modalBackground ?? builtInPreset?.modalBackground ?? base.modalBackground,
  }

  return {
    config: resolvedConfig,
    debug: {
      resolvedDeviceType: resolvedTypeToken,
      styleDeviceType: styleDeviceType || resolvedTypeToken,
      resolvedStyleId: resolvedConfig.modalStyleId || '',
      matchedOverrideKey: overrideMatch?.key || '',
      powerStatusOffKey,
    },
  }
}
